import { Injectable, NgZone, OnDestroy, inject, signal } from '@angular/core';
import { AuthService } from '../auth/auth.service';

export const INACTIVITY_LIMIT_MS = 20 * 60 * 1000;
const TRACKED_EVENTS: Array<keyof WindowEventMap> = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

@Injectable({ providedIn: 'root' })
export class InactivityService implements OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly authService = inject(AuthService);
  private readonly tracking = signal(false);
  private timeoutHandle: ReturnType<typeof setTimeout> | null = null;

  start(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.tracking()) {
      this.resetTimer();
      return;
    }

    this.tracking.set(true);
    this.zone.runOutsideAngular(() => {
      TRACKED_EVENTS.forEach((eventName) =>
        window.addEventListener(eventName, this.handleUserInteraction, { capture: true, passive: true })
      );
    });

    this.resetTimer();
  }

  stop(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (!this.tracking()) {
      return;
    }

    this.tracking.set(false);
    this.zone.runOutsideAngular(() => {
      TRACKED_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, this.handleUserInteraction, true)
      );
    });
    this.clearTimer();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private readonly handleUserInteraction = () => {
    this.resetTimer();
  };

  private resetTimer(): void {
    if (!this.tracking()) {
      return;
    }

    this.clearTimer();
    this.zone.runOutsideAngular(() => {
      this.timeoutHandle = window.setTimeout(
        () => this.zone.run(() => this.handleInactivityTimeout()),
        INACTIVITY_LIMIT_MS
      );
    });
  }

  private handleInactivityTimeout(): void {
    if (this.authService.hasValidAccessToken()) {
      this.resetTimer();
      return;
    }

    this.stop();
    void this.authService.logoutDueToInactivity();
  }

  private clearTimer(): void {
    if (!this.timeoutHandle) {
      return;
    }

    clearTimeout(this.timeoutHandle);
    this.timeoutHandle = null;
  }
}
